import React, { useMemo } from 'react';
import classNames from 'classnames';
import MetricsCard from '@/pages/dashboard/components/MetricsCard';
import { dynamicString } from '@home/utils/dynamicString';
import MetricsCardTrendChart from '@/pages/dashboard/components/MetricsCardTrendChart';
import { generateYAxisLabels } from './legendComponent';
import { formatMetricData } from './utils';
import './index.scss';

/**
 * alignmentConfig 形如：
 * {
 *   version_aligned: { titleCn, titleEn, metricKey, dodConfig, dodDataMap, trendConfig },
 *   natural_day_aligned: { titleCn, titleEn, metricKey, dodConfig, dodDataMap, trendConfig },
 * }
 * 命中 alignmentValue 后，按 key 浅合并覆盖原 props；缺省则回退到 props 上的同名字段。
 */
type IAlignmentOverride = Partial<{
  titleCn: string;
  titleEn: string;
  metricKey: string;
  formatRule: string;
  dodConfig: any[];
  dodDataMap: Record<string, string>;
  trendConfig: any;
}>;

const ChangeAnalysisCard = (props: any) => {
  const {
    imgUrl,
    className,
    apiDataKey = 'card_data',
    data: propsData,
    filter,
    showArrowIcon,
    // 可被 alignmentConfig 覆盖的字段
    titleEn: rawTitleEn,
    titleCn: rawTitleCn,
    subtitleEn,
    tipsEn,
    tipsCn,
    formatRule: rawFormatRule,
    metricKey: rawMetricKey,
    dodConfig: rawDodConfig,
    dodDataMap: rawDodDataMap,
    trendConfig: rawTrendConfig,
    // 对齐切换相关
    alignmentConfig,
    ...restProps
  } = props;

  // 从 data 中读取 alignment 值（由上层 Switcher 通过 changeData 写入）
  const alignmentValue: string = propsData?.alignment || 'version_aligned';

  // 解析最终生效的卡片配置：alignmentConfig[alignmentValue] 浅合并覆盖原 props
  const resolved = useMemo(() => {
    const override: IAlignmentOverride =
      alignmentConfig?.[alignmentValue] || {};
    return {
      titleEn: override.titleEn ?? rawTitleEn,
      titleCn: override.titleCn ?? rawTitleCn,
      formatRule: override.formatRule ?? rawFormatRule,
      metricKey: override.metricKey ?? rawMetricKey,
      dodConfig: override.dodConfig ?? rawDodConfig,
      dodDataMap: override.dodDataMap ?? rawDodDataMap,
      trendConfig: override.trendConfig ?? rawTrendConfig,
    };
  }, [
    alignmentConfig,
    alignmentValue,
    rawTitleEn,
    rawTitleCn,
    rawFormatRule,
    rawMetricKey,
    rawDodConfig,
    rawDodDataMap,
    rawTrendConfig,
  ]);

  const { data } = propsData?.[apiDataKey] || {};
  const { trend_dau_data: trendData, card_data: cardData } = data || {};

  const dodData = resolved.dodConfig?.map((item: any) => ({
    titleEn: item?.titleEn,
    titleCn: item?.titleCn,
    formatRule: item?.formatRule,
    value: cardData?.[item?.metricKey],
    hideBubbleContent: item?.hideBubbleContent,
    hideDodIcon: item?.hideDodIcon,
    style: item?.style,
    className: item?.className,
    thisValue: cardData?.[resolved.dodDataMap?.thisValue],
    lastValue: cardData?.[resolved.dodDataMap?.lastValue],
    thisTime: cardData?.[resolved.dodDataMap?.thisTime],
    lastTime: cardData?.[resolved.dodDataMap?.lastTime],
    ...item,
  }));

  return (
    <MetricsCard
      className={classNames('dbd-change-analysis-trend-card', className)}
      titleEn={dynamicString(resolved.titleEn, { ...propsData, ...filter })}
      titleCn={dynamicString(resolved.titleCn, { ...propsData, ...filter })}
      value={cardData?.[resolved.metricKey]}
      formatRule={dynamicString(resolved.formatRule, propsData)}
      subtitleEn={dynamicString(subtitleEn, propsData) || ''}
      tipsEn={dynamicString(tipsEn, propsData)}
      tipsCn={dynamicString(tipsCn, propsData)}
      imgUrl={imgUrl}
      showArrowIcon={showArrowIcon}
      dodData={dodData}
      metricsChart={
        resolved.trendConfig && (
          <MetricsCardTrendChart
            titleEn={dynamicString(resolved.titleEn, { ...propsData, ...filter })}
            titleCn={dynamicString(resolved.titleCn, { ...propsData, ...filter })}
            metricData={formatMetricData(trendData, resolved.trendConfig)}
            loading={false}
            error={false}
            filterVal={{ timeType: 'Week' }}
            componentMetrics={[resolved.trendConfig]}
            yAxisName={generateYAxisLabels(resolved.trendConfig)}
            {...restProps}
          />
        )
      }
      {...restProps}
    >
    </MetricsCard>
  );
};

export default ChangeAnalysisCard;
